import React from 'react';
import { Card, CardContent, Typography, Box, Tooltip } from '@mui/material';
import { ArrowUpward, ArrowDownward, InfoOutlined } from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';

interface ReportCardProps {
  title: string;
  value: string | number;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon: SvgIconComponent;
  color: string;
  subtitle?: string;
  loading?: boolean;
  tooltip?: string;
}

export const ReportCard: React.FC<ReportCardProps> = ({
  title,
  value,
  change,
  trend = 'neutral',
  icon: Icon,
  color,
  subtitle,
  loading = false,
  tooltip,
}) => {
  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        border: 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)`,
          borderRadius: '20px 20px 0 0',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#606060', fontWeight: 500 }}>
                {title}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} placement="top" arrow>
                  <InfoOutlined sx={{ fontSize: 14, color: '#9E9E9E', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
              {loading ? '...' : value}
            </Typography>
            {change && trend !== 'neutral' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {trend === 'up' ? (
                  <ArrowUpward sx={{ fontSize: 16, color: '#009A75' }} />
                ) : (
                  <ArrowDownward sx={{ fontSize: 16, color: '#FF3D00' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: trend === 'up' ? '#009A75' : '#FF3D00',
                    fontWeight: 600,
                  }}
                >
                  {change}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" sx={{ color: '#909090', ml: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )}
            {!change && subtitle && (
              <Typography variant="body2" sx={{ color: '#909090' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: { xs: 40, sm: 56 },
              height: { xs: 40, sm: 56 },
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 28, color: color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
